// src/pages/auth/GoogleCallbackPage.tsx
// ✅ UPDATED: Now supports profile creation during OAuth signup via localStorage
import { Suspense, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext.tsx'
import LoadingSpinner from '@/components/custom-ui/LoadingSpinner.tsx'
import CONFIG from '@/lib/config'

export default function GoogleCallbackPageWrapper() {
    return (
        <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-[#1A4D4F]">
                <div className="bg-white p-8 rounded-lg shadow-lg">
                    <LoadingSpinner size="large" text="Verifierar inloggning..." />
                </div>
            </div>
        }>
            <GoogleCallbackPage />
        </Suspense>
    )
}

function GoogleCallbackPage() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { handleGoogleCallback } = useAuth()
    const processedRef = useRef(false)

    // Get configuration from environment or use defaults
    const getRedirectConfig = useCallback(() => {
        // Use the full callback URL from config
        const redirectUri = CONFIG.GOOGLE_OAUTH_CALLBACK_FULL_URL

        // Get user type from URL params or default to 'candidate'
        const userType = searchParams.get('state') ||
            searchParams.get('user_type') ||
            'candidate'

        // Get success/failure redirect paths from env or use defaults
        const successRedirect = import.meta.env.VITE_OAUTH_SUCCESS_REDIRECT || '/dashboard'
        const failureRedirect = import.meta.env.VITE_OAUTH_FAILURE_REDIRECT || '/'

        return {
            redirectUri,
            userType,
            successRedirect,
            failureRedirect
        }
    }, [searchParams])

    const processGoogleCallback = useCallback(async () => {
        // Prevent multiple processing using ref instead of global variable
        if (processedRef.current) {
            return
        }

        const code = searchParams.get('code')
        const error = searchParams.get('error')
        const config = getRedirectConfig()

        // Handle OAuth errors
        if (error) {
            console.error('OAuth error:', error)
            const errorDescription = searchParams.get('error_description')
            if (errorDescription) {
                console.error('Error description:', errorDescription)
            }
            navigate(`${config.failureRedirect}?error=oauth_error`)
            return
        }

        // Check for authorization code
        if (!code) {
            console.error('No authorization code found in URL')
            navigate(`${config.failureRedirect}?error=no_code`)
            return
        }

        // ✅ NEW: Retrieve stored profile data from localStorage
        const storedProfile = localStorage.getItem('oauth_profile_data')
        const extra = storedProfile ? JSON.parse(storedProfile) : undefined

        // Set processed flag immediately
        processedRef.current = true

        try {
            // ✅ UPDATED: Pass extra parameter for profile creation
            const result = await handleGoogleCallback(
                code,
                config.redirectUri,
                config.userType,
                extra  // ✅ Profile data passed here - backend creates user + profile atomically!
            )

            if (result.success) {
                // ✅ NEW: Clean up localStorage after successful login
                if (storedProfile) {
                    localStorage.removeItem('oauth_profile_data')
                }

                navigate(config.successRedirect)
            } else {
                console.error('Google login failed:', result.error)

                // Clean up localStorage on failure too
                if (storedProfile) {
                    localStorage.removeItem('oauth_profile_data')
                }

                navigate(`${config.failureRedirect}?error=login_failed`)
            }
        } catch (error) {
            console.error('Error processing Google callback:', error)

            // Clean up localStorage on error
            if (storedProfile) {
                localStorage.removeItem('oauth_profile_data')
            }

            navigate(`${config.failureRedirect}?error=callback_error`)
        }
    }, [searchParams, navigate, handleGoogleCallback, getRedirectConfig])

    useEffect(() => {
        // Small delay to ensure component is fully mounted
        const timer = setTimeout(() => {
            processGoogleCallback()
        }, 100)

        return () => clearTimeout(timer)
    }, [processGoogleCallback])

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#1A4D4F]">
            <div className="bg-white p-8 rounded-lg shadow-lg">
                <LoadingSpinner
                    size="large"
                    text="Loggar in med Google..."
                />
            </div>
        </div>
    )
}